<template>
  <v-dialog v-model="dialog" max-width="600" persistent>
    <template v-slot:activator="{ props }">
      <v-btn
        v-bind="props"
        color="white"
        :prepend-icon="mdiCalculator"
        variant="outlined"
      >
        {{ $t("projections.calculator.title") }}
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="text-h5">{{ $t("projections.calculator.title") }}</span>
        <v-btn :icon="mdiClose" variant="text" @click="closeDialog"></v-btn>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model.number="form.amountInEuros"
                :label="$t('projections.calculator.amountLabel')"
                type="number"
                min="0"
                step="0.01"
                suffix="€"
                variant="outlined"
                :rules="[rules.required, rules.positive]"
                @input="calculatePointsDebounced"
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="form.categoryId"
                :items="categories"
                item-title="name"
                item-value="id"
                :label="$t('projections.calculator.categoryLabel')"
                variant="outlined"
                :rules="[rules.required]"
                @update:model-value="calculatePointsDebounced"
              ></v-select>
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="form.statusId"
                :items="statuses"
                item-title="label"
                item-value="id"
                :label="$t('projections.calculator.statusLabel')"
                variant="outlined"
                :rules="[rules.required]"
                @update:model-value="calculatePointsDebounced"
              ></v-select>
            </v-col>

            <v-col cols="12" v-if="loading">
              <v-card color="white" variant="elevated" elevation="3">
                <v-card-text class="py-8">
                  <div class="text-center">
                    <v-progress-circular
                      color="#ffb500"
                      size="64"
                      width="6"
                      indeterminate
                    ></v-progress-circular>
                    <p class="text-body-1 mt-4" style="color: #616161">
                      {{ $t("projections.calculator.calculating") }}
                    </p>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" v-if="result && !loading">
              <v-card color="white" variant="elevated" elevation="3">
                <v-card-text class="py-6">
                  <div class="text-center">
                    <p
                      class="text-subtitle-1 mb-3 font-weight-medium"
                      style="color: #424242"
                    >
                      {{ $t("projections.calculator.resultLabel") }}
                    </p>
                    <p class="text-h3 font-weight-bold" style="color: #ffb500">
                      {{ formatNumber(result.points) }}
                      <span class="text-h6" style="color: #424242">{{
                        $t("projections.calculator.pointsUnit")
                      }}</span>
                    </p>
                    <p
                      class="text-body-1 mt-3 font-weight-medium"
                      style="color: #616161"
                    >
                      {{
                        $t("projections.calculator.conversionRate", {
                          rate: formatNumber(result.conversionRate),
                        })
                      }}
                    </p>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" v-if="error">
              <v-alert type="error" variant="outlined" closable>
                {{ error }}
              </v-alert>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="#ffb500" variant="flat" @click="closeDialog">
          {{ $t("close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import { mdiCalculator, mdiClose } from "@mdi/js";

export default {
  name: "PointsCalculatorDialog",
  data() {
    return {
      mdiCalculator,
      mdiClose,
      dialog: false,
      loading: false,
      error: null,
      result: null,
      form: {
        amountInEuros: null,
        categoryId: null,
        statusId: null,
      },
      categories: [
        { id: 1, name: "Service" },
        { id: 2, name: "Capital" },
        { id: 3, name: "Implant" },
        { id: 4, name: "Consommable" },
      ],
      statuses: [
        { id: 1, label: "Statut 1" },
        { id: 2, label: "Statut 2" },
        { id: 3, label: "Statut 3" },
        { id: 4, label: "Statut 4" },
      ],
      rules: {
        required: (value) => !!value || this.$t("required"),
        positive: (value) => value >= 0 || this.$t("mustBePositive"),
      },
      debounceTimeout: null,
    };
  },
  computed: {
    isFormValid() {
      return (
        this.form.amountInEuros > 0 &&
        this.form.categoryId !== null &&
        this.form.statusId !== null
      );
    },
  },
  methods: {
    ...mapActions("projections", ["calculatePoints"]),

    /**
     * Calcule les points avec un délai pour éviter trop de requêtes
     */
    calculatePointsDebounced() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        if (this.isFormValid) {
          this.calculatePoints();
        }
      }, 500);
    },

    /**
     * Appelle l'API pour calculer les points
     */
    async calculatePoints() {
      if (!this.isFormValid) {
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        this.result = await this.$store.dispatch(
          "projections/calculatePoints",
          this.form,
        );
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          this.$t("projections.calculator.error");
      } finally {
        this.loading = false;
      }
    },

    /**
     * Formate un nombre avec séparateur de milliers
     */
    formatNumber(value) {
      if (!value && value !== 0) return "0";
      return new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },

    /**
     * Ferme le dialog et réinitialise le formulaire
     */
    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },

    /**
     * Réinitialise le formulaire
     */
    resetForm() {
      this.form = {
        amountInEuros: null,
        categoryId: null,
        statusId: null,
      };
      this.result = null;
      this.error = null;
    },
  },
  beforeUnmount() {
    clearTimeout(this.debounceTimeout);
  },
};
</script>

<style scoped>
.text-primary {
  color: #ffb500 !important;
}
</style>
